import { Component, ChangeDetectionStrategy, signal, inject, PLATFORM_ID, Input, computed } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ContextService } from '../../services/context.service';

@Component({
  selector: 'app-background-aura',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Base Background Color -->
    @if (!hideBase) {
      <div class="fixed inset-0 bg-slate-50 dark:bg-[#0a1118] -z-[20]"></div>
    }

    <!-- Noise Texture (Digital Silk) -->
    <div class="aura-noise"></div>

    <!-- Optimized LED Strip Footer (Atmospheric & Near-Invisible) -->
    <div class="led-strip-container -z-[10] select-none" 
         [class.opacity-0]="reducedMotion()"
         [class.full-mode]="cornerMode === 'full'"
         [class.corner-mode]="cornerMode === 'bottom-left'"
         [style.height]="customHeight || '500px'">
      
      <!-- Primary Deep Blue Wave -->
      <div class="led-glow animate-led-wave led-wave-primary" [class.pause-animations]="isTyping() || isModalOpen()">
      </div>

      <!-- Emerald/Teal Wave -->
      <div class="led-glow animate-led-wave led-wave-secondary" [class.pause-animations]="isTyping() || isModalOpen()">
      </div>

      <!-- Cyan Highlight -->
      @if (!isWeakHardware) {
        <div class="led-glow animate-led-wave led-wave-tertiary" [class.pause-animations]="isTyping() || isModalOpen()">
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      position: var(--aura-pos, fixed);
      inset: 0;
      z-index: -10;
      pointer-events: none;
      contain: strict; /* Isolar layout/paint do restante da página */
    }
    
    :host-context(.relative-aura) {
      --aura-pos: absolute;
    }

    .led-strip-container {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      overflow: hidden;
      pointer-events: none;
    }

    /* O mask-image forçava um passo de composite com blend caríssimo. 
       Como já trocamos o linear-gradient por radial-gradient, a fumaça "apaga" automaticamente nas bordas
       sem precisarmos do contêiner cortando (mask) ela via hardware! */
    .full-mode {
       /* Nenhuma mask necessária, o radiano interno morre no transparente */
    }

    .corner-mode {
       /* Apenas deslocamos as ondas */
      transform: translate(-20%, 30%) scale(1.5);
    }

    /* OTIMIZADO: Classes CSS estáticas com radial-gradient (Simulam o Blur com zero esforço da GPU) */
    .led-wave-primary {
      background: radial-gradient(ellipse at center, var(--aura-main-color) 0%, transparent 60%);
      animation-duration: 22s;
      opacity: 0.3;
    }

    .led-wave-secondary {
      background: radial-gradient(ellipse at center, var(--aura-accent-color) 0%, transparent 60%);
      animation-duration: 28s;
      animation-delay: -10s;
      opacity: 0.35;
    }

    .led-wave-tertiary {
      background: radial-gradient(ellipse at center, var(--aura-primary-color) 0%, transparent 60%);
      animation-duration: 32s;
      animation-delay: -15s;
      opacity: 0.1;
    }

    /* Hardware fraco - opacidade reduzida */
    :host-context(.weak-hardware) .led-wave-primary {
      opacity: 0.2;
    }
    :host-context(.weak-hardware) .led-wave-secondary {
      opacity: 0.25;
    }

    .pause-animations {
      animation-play-state: paused !important;
    }
  `]
})
export class BackgroundAuraComponent {
  @Input() hideBase = false;
  @Input() customHeight?: string;
  @Input() cornerMode: 'bottom-left' | 'full' = 'full';

  private platformId = inject(PLATFORM_ID);

  context = inject(ContextService);
  isTyping = computed(() => this.context.isUserTyping());
  isModalOpen = computed(() => this.context.isModalOpen());

  reducedMotion = signal(false);
  isWeakHardware = false;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.set(mediaQuery.matches);

      mediaQuery.addEventListener('change', (e) => {
        this.reducedMotion.set(e.matches);
      });

      const nav = navigator as any;
      if (nav.hardwareConcurrency && nav.hardwareConcurrency <= 4) {
        this.isWeakHardware = true;
      }
      if (nav.deviceMemory && nav.deviceMemory <= 4) {
        this.isWeakHardware = true;
      }

      if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(nav.userAgent)) {
        this.isWeakHardware = true;
      }
    }
  }
}

